// GuestPassSummary.jsx
import React, { useEffect, useState, forwardRef, useMemo } from "react";
import {
    getGuestPassReservations,
    getGuestPassLeaderboard,
    getPlans,
} from "../services/apiServices";
import {
    MDBDatatable, MDBIcon, MDBBadge, MDBBtn,
    MDBModal, MDBModalBody, MDBModalContent, MDBModalDialog,
    MDBModalFooter, MDBModalHeader, MDBModalTitle,
} from "mdb-react-ui-kit";
import DatePicker from "react-datepicker";
import calImg from "./../components/images/calendar.svg";
import ExcelExportButton from "../components/exportToExcel";
import PageError from "./PageError";
import Loader from "../components/Loader";
import { useAnalytics } from "../hooks/useAnalytics";
import "react-datepicker/dist/react-datepicker.css";
import { formatDate, formatDateTimeWithTZ } from "../utils/DateFormat";
import { BasePlanName } from "../utils/BasePlanName";

const GuestPassSummary = () => {
    const { trackButtonClick } = useAnalytics();

    const [guestPasses, setGuestPasses] = useState([]);
    const [filteredGuestPasses, setFilteredGuestPasses] = useState([]);
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(false);
    const [plans, setPlans] = useState([]);

    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedGuestPass, setSelectedGuestPass] = useState(null);
    const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);

    // UI filter state
    const [filters, setFilters] = useState({
        search: "",
        status: "all",
    });
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;

    // Applied filters (for API calls - excludes search)
    const [appliedFilters, setAppliedFilters] = useState({
        status: "all",
        startDate: null,
        endDate: null,
        page: 1,
        limit: 30,
    });

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 30,
        total: 0,
        pages: 1
    });

    const [basicData, setBasicData] = useState({
        columns: ["S.No", "Member Name", "Referred By", "Phone", "Status", "Date", "Plan"],
        rows: [],
    });

    const CalenderInput = forwardRef(({ value, onClick }, ref) => (
        <span className="gym-input forminput h-40">
            {value === "" ? "Custom Range" : value}&nbsp;
            <img src={calImg} onClick={onClick} ref={ref} alt="Custom Range" title="Select Custom Range" />
        </span>
    ));

    // Fetch plans on mount
    useEffect(() => {
        fetchPlans();
        fetchLeaderboard();
    }, []);

    // Fetch plans function
    const fetchPlans = async () => {
        try {
            let gymid = JSON.parse(localStorage.getItem("loggedUserInfo")) ?
                JSON.parse(localStorage.getItem("loggedUserInfo")).gymid : "";

            const planResponse = await getPlans(gymid);

            if (!planResponse || planResponse === "false") {
                setApiError(true);
            } else {
                const plansData = await planResponse.json();
                if (plansData) {
                    setPlans(plansData);
                }
            }
        } catch (error) {
            console.error("Error fetching plans:", error);
            setApiError(true);
        }
    };

    // Re-fetch whenever appliedFilters changes (excluding search)
    useEffect(() => {
        fetchGuestPasses(appliedFilters);
    }, [appliedFilters]);

    const handleLimitChange = (limitValue) => {
        setAppliedFilters(prev => ({
            ...prev,
            page: 1,
            limit: limitValue
        }));
    };


    const fetchGuestPasses = async (cf) => {
        setLoading(true);
        try {
            const loggedUser = JSON.parse(localStorage.getItem("loggedUserInfo"));
            const gymId = loggedUser.gymid;

            const params = {
                gymId,
                status: cf.status === "all" ? "" : cf.status,
                page: cf.page || 1,
                limit: cf.limit || 30,
                sortBy: "CHECKED_IN",
                sortOrder: "desc",
            };


            if (cf.startDate && cf.endDate) {
                // Set to start of day and end of day for proper range
                const start = new Date(cf.startDate);
                start.setHours(0, 0, 0, 0);

                const end = new Date(cf.endDate);
                end.setHours(23, 59, 59, 999);

                params.startDate = start.toISOString();
                params.endDate = end.toISOString();
                // Based on your data, it looks like createdAt is the field to filter on
                params.dateType = "createdAt"; // Changed from "checkedIn" to "createdAt"
            }

            console.log("984867SendingparamstoAPI:", params); // Debug log

            const response = await getGuestPassReservations(params);

            if (!response || response === "false") {
                setApiError(true);
                setLoading(false);
                return;
            }

            const data = await response.json();
            console.log("API Response:984867", data); // Debug log

            if (data.success) {
                setGuestPasses(data.data.guestPasses || []);
                setFilteredGuestPasses(data.data.guestPasses || []);
                setPagination(data.data.pagination || { page: 1, limit: 30, total: 0, pages: 1 });
            } else {
                // If API returns success false, still set empty arrays
                setGuestPasses([]);
                setFilteredGuestPasses([]);
            }
        } catch (e) {
            console.error("Error fetching guest passes:", e);
            setApiError(true);
        }
        setLoading(false);
    };

    // Frontend search filter effect
    useEffect(() => {
        if (!guestPasses.length) {
            setFilteredGuestPasses([]);
            return;
        }

        let filtered = [...guestPasses];

        // Apply search filter (case-insensitive)
        if (filters.search && filters.search.trim() !== '') {
            const searchTerm = filters.search.toLowerCase().trim();

            filtered = filtered.filter(item => {
                // Search in guest name (referred member)
                const guestName = item.referredMemberId
                    ? `${item.referredMemberId.fname || ''} ${item.referredMemberId.lname || ''}`.toLowerCase()
                    : (item.guestName || '').toLowerCase();

                // Search in referred by name
                const referredByName = item.referringMemberId
                    ? `${item.referringMemberId.fname || ''} ${item.referringMemberId.lname || ''}`.toLowerCase()
                    : '';

                // Search in guest phone
                const guestPhone = item.referredMemberId?.number
                    ? item.referredMemberId.number.toLowerCase()
                    : (item.guestPhone || '').toLowerCase();

                // Search in referred by phone
                const referredByPhone = item.referringMemberId?.number
                    ? item.referringMemberId.number.toLowerCase()
                    : '';

                return guestName.includes(searchTerm) ||
                    referredByName.includes(searchTerm) ||
                    guestPhone.includes(searchTerm) ||
                    referredByPhone.includes(searchTerm);
            });
        }

        setFilteredGuestPasses(filtered);
    }, [filters.search, guestPasses]);

    const fetchLeaderboard = async () => {
        try {
            const gymId = JSON.parse(localStorage.getItem("loggedUserInfo")).gymid;
            const response = await getGuestPassLeaderboard(gymId, "all");
            if (response && response !== "false") {
                const data = await response.json();
                if (data.success) setLeaderboard(data.data || []);
            }
        } catch (e) {
            console.error(e);
        }
    };

    // Helper function to get guest name
    const getGuestName = (item) => {
        if (item.referredMemberId) {
            return `${item.referredMemberId.fname || ''} ${item.referredMemberId.lname || ''}`.trim() || 'N/A';
        }
        return item.guestName || 'N/A';
    };

    // Helper function to get referred by name
    const getReferredByName = (item) => {
        if (item.referringMemberId) {
            return `${item.referringMemberId.fname || ''} ${item.referringMemberId.lname || ''}`.trim() || 'N/A';
        }
        return 'N/A';
    };

    // Helper function to get phone number
    const getPhoneNumber = (item) => {
        if (item.referredMemberId?.number) {
            return item.referredMemberId.number;
        }
        return item.guestPhone || '-';
    };

    // Helper function to get the latest plan display name
    const getLatestPlanDisplayName = (guestPassItem) => {
        if (!guestPassItem) return "N/A";

        // For converted guests, show only the plan they converted with (last plan)
        if (guestPassItem.status === "CONVERTED" && guestPassItem.convertedTransactions && guestPassItem.convertedTransactions.length > 0) {
            // Get the last transaction (most recent)
            const lastTransaction = guestPassItem.convertedTransactions[guestPassItem.convertedTransactions.length - 1];

            if (lastTransaction && lastTransaction.planId) {
                // Try to find the plan name
                if (plans && plans.length > 0) {
                    // Find in main plans
                    let foundPlan = plans.find(p => p._id === lastTransaction.planId);

                    if (foundPlan) {
                        if (foundPlan.baseplan) {
                            return `${BasePlanName(plans, foundPlan.baseplan)} - ${foundPlan.name}`;
                        }
                        return foundPlan.name;
                    }

                    // Check in subplans
                    for (const plan of plans) {
                        if (plan.subplans && plan.subplans.length > 0) {
                            const foundSubplan = plan.subplans.find(sp => sp._id === lastTransaction.planId);
                            if (foundSubplan) {
                                if (foundSubplan.baseplan) {
                                    return `${BasePlanName(plans, foundSubplan.baseplan)} - ${foundSubplan.name}`;
                                }
                                return foundSubplan.name;
                            }
                        }
                    }
                }

                // If plan not found in plans array, check if it has name property
                if (lastTransaction.planId.name) {
                    return lastTransaction.planId.name;
                }

                // Return plan ID as fallback
                return "Plan ID: " + lastTransaction.planId.toString().substring(0, 8) + "...";
            }
        }

        // For non-converted guests, check if they have a plan
        if (guestPassItem.checkinplanid && Array.isArray(guestPassItem.checkinplanid) && guestPassItem.checkinplanid.length > 0) {
            // For checked-in guests, show their current plan
            const planId = guestPassItem.checkinplanid[guestPassItem.checkinplanid.length - 1];

            if (plans && plans.length > 0) {
                // Find in main plans
                let foundPlan = plans.find(p => p._id === planId);

                if (foundPlan) {
                    if (foundPlan.baseplan) {
                        return `${BasePlanName(plans, foundPlan.baseplan)} - ${foundPlan.name}`;
                    }
                    return foundPlan.name;
                }

                // Check in subplans
                for (const plan of plans) {
                    if (plan.subplans && plan.subplans.length > 0) {
                        const foundSubplan = plan.subplans.find(sp => sp._id === planId);
                        if (foundSubplan) {
                            if (foundSubplan.baseplan) {
                                return `${BasePlanName(plans, foundSubplan.baseplan)} - ${foundSubplan.name}`;
                            }
                            return foundSubplan.name;
                        }
                    }
                }
            }

            // If plan is just an ID string
            if (typeof planId === 'string') {
                // Check if it might be a plan name directly
                if (planId.includes('plan') || planId.includes('Plan')) {
                    return planId;
                }
                return "Plan ID: " + planId.substring(0, 8) + "...";
            }

            return "Plan ID: " + planId;
        }

        // Fallback to existing fields
        if (guestPassItem.planname) return guestPassItem.planname;
        if (guestPassItem.planId) {
            if (typeof guestPassItem.planId === 'object') {
                if (guestPassItem.planId.baseplan) {
                    return `${BasePlanName(plans, guestPassItem.planId.baseplan)} - ${guestPassItem.planId.name}`;
                }
                return guestPassItem.planId.name;
            }
        }

        return "N/A";
    };

    useEffect(() => {
        const tableRows = [];

        if (filteredGuestPasses && filteredGuestPasses.length > 0) {
            filteredGuestPasses.forEach((item, index) => {
                const row = [];

                // S.No
                row.push(index + 1);

                // Guest Name - Store as string for export
                const guestName = getGuestName(item);
                row.push(guestName);

                // Referred By - Store as string for export
                const referredByName = getReferredByName(item);
                row.push(referredByName);

                // Guest Phone - Store as string for export
                const phoneNumber = getPhoneNumber(item);
                row.push(phoneNumber);

                // Status - Store as string for export
                row.push(item.status || 'N/A');

                row.push(formatDate(item.updatedAt))
                // Plan - Store as string for export
                const planName = getLatestPlanDisplayName(item);
                row.push(planName);

                tableRows.push(row);
            });
        }

        setBasicData({
            columns: [
                "S.No",
                "Member Name",
                "Referred By",
                " Phone",
                "Status",
                "Date",
                "Plan",
            ],
            rows: tableRows,
        });
    }, [filteredGuestPasses, plans]);

    // Update the details modal to show all plans if needed
    const getPlanDisplayNameForDetails = (item) => {
        if (!item) return "N/A";

        // For details modal, we can show all plans if available
        if (item.checkinplanid && Array.isArray(item.checkinplanid) && item.checkinplanid.length > 1) {
            const planNames = [];
            item.checkinplanid.forEach(planId => {
                if (plans && plans.length > 0) {
                    let foundPlan = plans.find(p => p._id === planId);
                    if (foundPlan) {
                        if (foundPlan.baseplan) {
                            planNames.push(`${BasePlanName(plans, foundPlan.baseplan)} - ${foundPlan.name}`);
                        } else {
                            planNames.push(foundPlan.name);
                        }
                    } else {
                        // Check in subplans
                        for (const plan of plans) {
                            if (plan.subplans && plan.subplans.length > 0) {
                                const foundSubplan = plan.subplans.find(sp => sp._id === planId);
                                if (foundSubplan) {
                                    if (foundSubplan.baseplan) {
                                        planNames.push(`${BasePlanName(plans, foundSubplan.baseplan)} - ${foundSubplan.name}`);
                                    } else {
                                        planNames.push(foundSubplan.name);
                                    }
                                    break;
                                }
                            }
                        }
                    }
                }
            });
            if (planNames.length > 0) {
                return planNames.join(" → ");
            }
        }

        // If only one plan or no multiple plans, use the latest plan display
        return getLatestPlanDisplayName(item);
    };

    // Helpers
    const getStatusColor = (status) => {
        switch (status) {
            case "CHECKED_IN": return "primary";
            case "CONVERTED": return "success";
            default: return "light";
        }
    };

    const handleRowClick = (rowData) => {
        const index = rowData.index;

        if (index !== undefined && filteredGuestPasses[index]) {
            setSelectedGuestPass(filteredGuestPasses[index]);
            setShowDetailsModal(true);
        }
    };
    const applyHandler = () => {
        setAppliedFilters({
            status: filters.status,
            startDate: startDate || null,
            endDate: endDate || null,
            page: 1, // Reset to first page when applying new filters
            limit: 30,
        });
    };

    const handleClear = () => {
        setFilters({ search: "", status: "all" });
        setDateRange([null, null]);
        setAppliedFilters({
            status: "all",
            startDate: null,
            endDate: null,
            page: 1,
            limit: 30
        });
    };

    const handlePageChange = (newPage) => {
        setAppliedFilters((prev) => ({ ...prev, page: newPage }));
    };

const handleExport = async () => {
    const exportRows = filteredGuestPasses.map((item, index) => [
        index + 1,
        getGuestName(item),
        getReferredByName(item),
        getPhoneNumber(item),
        item.status || 'N/A',
        formatDate(item?.updatedAt),        
        getLatestPlanDisplayName(item)  
    ]);

    const exportData = {
        columns: basicData.columns,
        rows: exportRows,
    };

    await ExcelExportButton(exportData, "GuestPassSummary");
    await trackButtonClick("GuestPassSummary", "Export_GuestPasses", "staff", {});
};

    const handleReload = () => window.location.reload();

    // Render
    return (
        <>
            {apiError ? (
                <PageError handleReload={handleReload} />
            ) : (
                <div>
                    {/* Filters */}
                    <div className="trainerSummeryFilters d-flex align-items-center mb-3 mt-4 flex-wrap">
                        <div className="gym-section w-100-mobile ms-2">
                            <label className="fw-bold">Search:</label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Guest name / phone"
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            />
                        </div>

                        <div className="gym-section w-100-mobile ms-2">
                            <label className="fw-bold">Status:</label>
                            <select
                                className="form-select form-select-sm"
                                value={filters.status}
                                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            >
                                <option value="all">All</option>
                                <option value="CHECKED_IN">Checked In</option>
                                <option value="CONVERTED">Converted</option>
                            </select>
                        </div>

                        <div className="gym-section w-100-mobile ms-2">
                            <label className="fw-bold">Date Range:</label>
                            <DatePicker
                                dateFormat={"d/M/yyyy"}
                                selectsRange
                                startDate={startDate}
                                endDate={endDate}
                                onChange={(update) => setDateRange(update)}
                                isClearable={false}
                                customInput={<CalenderInput />}
                                placeholderText="Select date range"
                            />
                        </div>

                        <div className="mob-div-center mt-3 pt-1 filterApplyMobile">
                            <MDBBtn className="ms-2" onClick={applyHandler}>Apply</MDBBtn>
                            <MDBBtn className="ms-2" color="secondary" onClick={handleClear}>Clear</MDBBtn>
                            <MDBBtn className="ms-2" color="success" onClick={handleExport}>
                                <MDBIcon fas icon="download" className="me-1" />Export
                            </MDBBtn>
                            <MDBBtn className="ms-2" color="info" onClick={() => setShowLeaderboardModal(true)}>
                                <MDBIcon fas icon="trophy" className="me-1" />Top Referrers
                            </MDBBtn>
                        </div>
                    </div>

                    {/* Main data table */}
                    <div className="trainerSummeryDataTableNew plan mt-2">
                        {loading ? (
                            <div className="loader-height"><Loader /></div>
                        ) : (
                            <>
                                <MDBDatatable
                                    className="sortOff trainerSummerySort large-scrool-table"
                                    entries={pagination.limit}
                                    paging={true}
                                    data={basicData}
                                    pages={pagination.pages}
                                    currentPage={pagination.page}
                                    onPageChange={handlePageChange}
                                    onRowClick={handleRowClick}
                                    clickableRows={true}
                                />
                                {/* <div className="text-end mt-2">
                                    <small className="text-muted">
                                        Showing {filteredGuestPasses.length === 0 ? 0 : 1} to{" "}
                                        {filteredGuestPasses.length} of {filteredGuestPasses.length} entries
                                        {guestPasses.length > 0 && filteredGuestPasses.length < guestPasses.length &&
                                            ` (filtered from ${guestPasses.length} total)`}
                                    </small>
                                </div> */}
                            </>
                        )}
                    </div>







                    {/* Details Modal */}
                    {/* <MDBModal open={showDetailsModal} setOpen={setShowDetailsModal} tabIndex="-1">
                        <MDBModalDialog size="lg">
                            <MDBModalContent>
                                <MDBModalHeader>
                                    <MDBModalTitle>Guest Pass Details</MDBModalTitle>
                                    <MDBBtn className="btn-close" color="none" onClick={() => setShowDetailsModal(false)} />
                                </MDBModalHeader>
                                <MDBModalBody>
                                    {selectedGuestPass && (
                                        <div>
                                            <div className="row mb-3">
                                                <div className="col-md-6">
                                                    <h6 className="fw-bold">Referred By</h6>
                                                    <p>
                                                        {selectedGuestPass.referringMemberId?.fname || ""} {selectedGuestPass.referringMemberId?.lname || ""}<br />
                                                        <small className="text-muted">
                                                            Phone: {selectedGuestPass.referringMemberId?.number || "N/A"}
                                                        </small>
                                                    </p>
                                                </div>
                                                <div className="col-md-6">
                                                    <h6 className="fw-bold">Guest Details</h6>
                                                    <p>
                                                        Name: {selectedGuestPass.referredMemberId?.fname || selectedGuestPass.guestName || "N/A"} {selectedGuestPass.referredMemberId?.lname || ""}<br />
                                                        Phone: {selectedGuestPass.referredMemberId?.number || selectedGuestPass.guestPhone || "N/A"}<br />
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="row mb-3">
                                                <div className="col-md-4">
                                                    <h6 className="fw-bold">Status</h6>
                                                    <MDBBadge color={getStatusColor(selectedGuestPass.status)} pill>
                                                        {selectedGuestPass.status}
                                                    </MDBBadge>
                                                </div>
                                                <div className="col-md-4">
                                                    <h6 className="fw-bold">Plan{selectedGuestPass.checkinplanid?.length > 1 ? 's' : ''}</h6>
                                                    <p>{getPlanDisplayNameForDetails(selectedGuestPass)}</p>
                                                    {selectedGuestPass.checkinplanid?.length > 1 && (
                                                        <small className="text-muted">
                                                            Total plans: {selectedGuestPass.checkinplanid.length}
                                                        </small>
                                                    )}
                                                </div>
                                                <div className="col-md-4">
                                                    <h6 className="fw-bold">Revenue</h6>
                                                    <p className="text-success fw-bold">
                                                        ₹{(selectedGuestPass.totalRevenueFromReferral || 0).toLocaleString()}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-3">
                                                    <h6 className="fw-bold">Created At</h6>
                                                    <p>{selectedGuestPass.createdAt ? formatDateTimeWithTZ(selectedGuestPass.createdAt) : "-"}</p>
                                                </div>
                                                <div className="col-md-3">
                                                    <h6 className="fw-bold">Last Updated</h6>
                                                    <p>{selectedGuestPass.updatedAt ? formatDateTimeWithTZ(selectedGuestPass.updatedAt) : "-"}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </MDBModalBody>
                                <MDBModalFooter>
                                    <MDBBtn color="secondary" onClick={() => setShowDetailsModal(false)}>Close</MDBBtn>
                                </MDBModalFooter>
                            </MDBModalContent>
                        </MDBModalDialog>
                    </MDBModal> */}

                    {/* Leaderboard Modal */}
                    <MDBModal open={showLeaderboardModal} setOpen={setShowLeaderboardModal} tabIndex="-1" size="xl">
                        <MDBModalDialog size="xl">
                            <MDBModalContent>
                                <MDBModalHeader>
                                    <MDBModalTitle>Top Referring Members</MDBModalTitle>
                                    <MDBBtn className="btn-close" color="none" onClick={() => setShowLeaderboardModal(false)} />
                                </MDBModalHeader>
                                <MDBModalBody>
                                    {leaderboard.length > 0 ? (
                                        <div className="table-responsive guest-table">
                                            <table className="table table-hover align-middle">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Rank</th>
                                                        <th>Member</th>
                                                        <th>Contact</th>
                                                        <th className="text-center">Referrals</th>
                                                        <th className="text-center">Converted</th>
                                                        <th className="text-end">Revenue</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {leaderboard.map((item, index) => (
                                                        <tr key={item._id}>
                                                            <td><strong>{index + 1}</strong></td>
                                                            <td><strong>{item.memberName || 'N/A'}</strong></td>
                                                            <td>{item.memberPhone || 'N/A'}</td>
                                                            <td className="text-center"><MDBBadge color="info" pill>{item.totalReferrals}</MDBBadge></td>
                                                            <td className="text-center"><MDBBadge color="success" pill>{item.converted || 0}</MDBBadge></td>
                                                            <td className="text-end"><strong>₹{(item.totalRevenue || 0).toLocaleString()}</strong></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-5">
                                            <p className="text-muted mb-0">No referral data available</p>
                                        </div>
                                    )}
                                </MDBModalBody>
                                <MDBModalFooter>
                                    <MDBBtn color="secondary" onClick={() => setShowLeaderboardModal(false)}>Close</MDBBtn>
                                </MDBModalFooter>
                            </MDBModalContent>
                        </MDBModalDialog>
                    </MDBModal>
                </div>
            )}
        </>
    );
};

export default GuestPassSummary;